// src/services/categoryService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { Category, CategoryFormData, CategoryWithSubcategories } from '../../../enums/product';

const CATEGORIES_COLLECTION = 'categories';
const STORAGE_PATH = 'categories';

export interface PaginatedCategoriesResult {
  categories: CategoryWithSubcategories[];
  totalCount: number;
  // These are kept for interface compatibility but are always null now —
  // callers no longer need to pass cursors back in.
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface GlobalCategoryStats {
  totalMainCategories: number;
  totalSubcategories: number;
  totalStarred: number;
  totalActive: number;
  totalInactive: number;
}

export class CategoryService {
  /* =====================================================
     IMAGE MANAGEMENT METHODS
     ===================================================== */
  static async uploadImage(categoryId: string, file: File): Promise<string> {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `${categoryId}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, `${STORAGE_PATH}/${fileName}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  static async deleteImage(categoryId: string, imageUrl: string): Promise<void> {
    try {
      const urlParts = imageUrl.split('/o/');
      if (urlParts.length < 2) return;
      const pathWithParams = urlParts[1];
      const filePath = decodeURIComponent(pathWithParams.split('?')[0]);
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
      const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
      await updateDoc(categoryRef, {
        imageUrl: null,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  /* =====================================================
     CATEGORY CRUD METHODS
     ===================================================== */
  static async createCategory(
    formData: CategoryFormData,
    userId: string,
    imageFile?: File | null
  ): Promise<string> {
    try {
      const categoryData: any = {
        name: formData.name,
        description: formData.description || null,
        parentId: formData.parentId || null,
        isActive: formData.isActive,
        isStarred: false,
        sortOrder: 0, // always 0 — not exposed in the UI
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        imageUrl: null,
      };
      const docRef = await addDoc(collection(db, CATEGORIES_COLLECTION), categoryData);
      if (imageFile && !formData.parentId) {
        const imageUrl = await this.uploadImage(docRef.id, imageFile);
        await updateDoc(docRef, { imageUrl });
      }
      return docRef.id;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  static async updateCategory(
    categoryId: string,
    formData: CategoryFormData,
    imageFile?: File | null
  ): Promise<void> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) throw new Error('Category not found');
      const currentData = categoryDoc.data();

      const updateData: any = {
        name: formData.name,
        description: formData.description || null,
        parentId: formData.parentId || null,
        isActive: formData.isActive,
        sortOrder: 0, // always 0 — not exposed in the UI
        updatedAt: serverTimestamp(),
      };

      if (formData.parentId && currentData.imageUrl) {
        await this.deleteImage(categoryId, currentData.imageUrl);
        updateData.imageUrl = null;
        updateData.isStarred = false;
      } else if (!formData.parentId && imageFile) {
        if (currentData.imageUrl) {
          try {
            const urlParts = currentData.imageUrl.split('/o/');
            if (urlParts.length >= 2) {
              const filePath = decodeURIComponent(urlParts[1].split('?')[0]);
              await deleteObject(ref(storage, filePath));
            }
          } catch (error) {
            console.error('Error deleting old image:', error);
          }
        }
        const imageUrl = await this.uploadImage(categoryId, imageFile);
        updateData.imageUrl = imageUrl;
      }

      await updateDoc(categoryRef, updateData);
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  static async deleteCategory(categoryId: string): Promise<void> {
    try {
      const subcategories = await this.getSubcategories(categoryId);
      if (subcategories.length > 0) {
        throw new Error('Cannot delete category that has subcategories. Delete subcategories first.');
      }
      const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      if (categoryDoc.exists()) {
        const categoryData = categoryDoc.data();
        if (categoryData.imageUrl) {
          try {
            await this.deleteImage(categoryId, categoryData.imageUrl);
          } catch (error) {
            console.error('Error deleting category image:', error);
          }
        }
      }
      await deleteDoc(categoryRef);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  /* =====================================================
     CATEGORY STATUS MANAGEMENT
     ===================================================== */
  static async toggleCategoryStatus(categoryId: string, isActive: boolean): Promise<void> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
      await updateDoc(categoryRef, { isActive, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error toggling category status:', error);
      throw error;
    }
  }

  static async toggleCategoryStarred(categoryId: string, isStarred: boolean): Promise<void> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) throw new Error('Category not found');
      if (categoryDoc.data().parentId) throw new Error('Only main categories can be starred');
      await updateDoc(categoryRef, { isStarred, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error toggling category starred:', error);
      throw error;
    }
  }

  /* =====================================================
     GLOBAL STATS
     ===================================================== */
  static async getGlobalStats(): Promise<GlobalCategoryStats> {
    try {
      const collectionRef = collection(db, CATEGORIES_COLLECTION);

      const [
        mainSnap,
        subSnap,
        starredSnap,
        activeMainSnap,
        activeSubSnap,
        inactiveMainSnap,
        inactiveSubSnap,
      ] = await Promise.all([
        getCountFromServer(query(collectionRef, where('parentId', '==', null))),
        getCountFromServer(query(collectionRef, where('parentId', '!=', null))),
        getCountFromServer(
          query(collectionRef, where('parentId', '==', null), where('isStarred', '==', true))
        ),
        getCountFromServer(
          query(collectionRef, where('parentId', '==', null), where('isActive', '==', true))
        ),
        getCountFromServer(
          query(collectionRef, where('parentId', '!=', null), where('isActive', '==', true))
        ),
        getCountFromServer(
          query(collectionRef, where('parentId', '==', null), where('isActive', '==', false))
        ),
        getCountFromServer(
          query(collectionRef, where('parentId', '!=', null), where('isActive', '==', false))
        ),
      ]);

      return {
        totalMainCategories: mainSnap.data().count,
        totalSubcategories: subSnap.data().count,
        totalStarred: starredSnap.data().count,
        totalActive: activeMainSnap.data().count + activeSubSnap.data().count,
        totalInactive: inactiveMainSnap.data().count + inactiveSubSnap.data().count,
      };
    } catch (error) {
      console.error('Error fetching global stats:', error);
      throw error;
    }
  }

  /* =====================================================
     PAGINATED CATEGORY RETRIEVAL

     HOW ARBITRARY PAGE JUMPING WORKS
     ─────────────────────────────────
     Firestore's client SDK does not expose offset(). The standard
     cursor approach (startAfter last doc of previous page) only works
     sequentially. To jump to any page we use a two-step "cursor seek":

     Step 1 — fetch only document IDs (no field data) for the first
              (page-1)*pageSize documents. This is cheap: Firestore
              counts these as reads but transfers almost no data because
              we select no fields — just the __name__ (document path).

     Step 2 — use the last ID doc from Step 1 as a startAfter cursor,
              then fetch the real pageSize documents with full data.

     For page 1 the seek step is skipped entirely (offset = 0).

     This gives correct results for any page in any order:
       page 1 → page 5 → page 2 → page 10  ✓

     NOTE: typeFilter has been removed from the UI and is no longer
     passed in. The service always queries main categories (parentId == null)
     and fetches their subcategories separately, which is the correct
     hierarchical display behaviour.
     ===================================================== */
  static async getCategoriesPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: boolean,
    starredFilter?: boolean
    // typeFilter removed — always queries main categories + their subcategories
  ): Promise<PaginatedCategoriesResult> {
    try {
      const collectionRef = collection(db, CATEGORIES_COLLECTION);

      // Always query main categories only for the paginated list.
      // Subcategories are fetched separately per parent below.
      const whereConstraints: any[] = [
        where('parentId', '==', null),
      ];

      if (statusFilter !== undefined) {
        whereConstraints.push(where('isActive', '==', statusFilter));
      }

      if (starredFilter !== undefined) {
        whereConstraints.push(where('isStarred', '==', starredFilter));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Step 0: get total count (0 doc reads) ────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...whereConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ───────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekQuery = query(
          collectionRef,
          ...whereConstraints,
          orderConstraint,
          limit(skipCount)
        );
        const seekSnapshot = await getDocs(seekQuery);
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ──────────────────────────────────────
      let pageQuery: Query<DocumentData>;

      if (cursorDoc) {
        pageQuery = query(
          collectionRef,
          ...whereConstraints,
          orderConstraint,
          startAfter(cursorDoc),
          limit(pageSize)
        );
      } else {
        pageQuery = query(
          collectionRef,
          ...whereConstraints,
          orderConstraint,
          limit(pageSize)
        );
      }

      const snapshot = await getDocs(pageQuery);

      // ── Map docs → Category objects ───────────────────────────────────────
      const rawCategories: Category[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          name: data.name,
          description: data.description,
          parentId: data.parentId,
          isActive: data.isActive !== false,
          isStarred: data.isStarred || false,
          sortOrder: data.sortOrder || 0,
          imageUrl: data.imageUrl,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          createdBy: data.createdBy,
        } as Category;
      });

      // ── Client-side search filter ─────────────────────────────────────────
      const searchLower = searchQuery?.trim().toLowerCase() ?? '';
      const filteredCategories = searchLower
        ? rawCategories.filter(
            cat =>
              cat.name.toLowerCase().includes(searchLower) ||
              (cat.description && cat.description.toLowerCase().includes(searchLower))
          )
        : rawCategories;

      // ── Fetch subcategories per parent in parallel ────────────────────────
      const categories: CategoryWithSubcategories[] = await Promise.all(
        filteredCategories.map(async mainCategory => {
          const subcategories = await this.getSubcategories(mainCategory.id!);
          const filteredSubs = searchLower
            ? subcategories.filter(
                sub =>
                  sub.name.toLowerCase().includes(searchLower) ||
                  (sub.description && sub.description.toLowerCase().includes(searchLower))
              )
            : subcategories;

          return { ...mainCategory, subcategories: filteredSubs };
        })
      );

      return {
        categories,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated categories:', error);
      throw error;
    }
  }

  /* =====================================================
     NON-PAGINATED CATEGORY RETRIEVAL (Legacy)
     ===================================================== */
  /** @deprecated Use getCategoriesPaginated instead */
  static async getCategories(): Promise<Category[]> {
    try {
      const q = query(
        collection(db, CATEGORIES_COLLECTION),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Category[];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  static async getMainCategories(): Promise<Category[]> {
    try {
      const q = query(
        collection(db, CATEGORIES_COLLECTION),
        where('parentId', '==', null),
        where('isActive', '==', true),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Category[];
    } catch (error) {
      console.error('Error fetching main categories:', error);
      throw error;
    }
  }

  static async getStarredCategories(): Promise<Category[]> {
    try {
      const q = query(
        collection(db, CATEGORIES_COLLECTION),
        where('parentId', '==', null),
        where('isActive', '==', true),
        where('isStarred', '==', true),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Category[];
    } catch (error) {
      console.error('Error fetching starred categories:', error);
      throw error;
    }
  }

  static async getSubcategories(parentId: string): Promise<Category[]> {
    try {
      const q = query(
        collection(db, CATEGORIES_COLLECTION),
        where('parentId', '==', parentId),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Category[];
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      throw error;
    }
  }

  /** @deprecated Use getCategoriesPaginated instead */
  static async getCategoriesWithSubcategories(): Promise<CategoryWithSubcategories[]> {
    try {
      const allCategories = await this.getCategories();
      const mainCategories = allCategories.filter(cat => !cat.parentId);
      const subcategoriesMap = new Map<string, Category[]>();
      allCategories.forEach(cat => {
        if (cat.parentId) {
          if (!subcategoriesMap.has(cat.parentId)) subcategoriesMap.set(cat.parentId, []);
          subcategoriesMap.get(cat.parentId)!.push(cat);
        }
      });
      return mainCategories.map(category => ({
        ...category,
        subcategories: subcategoriesMap.get(category.id!) || [],
      }));
    } catch (error) {
      console.error('Error fetching categories with subcategories:', error);
      throw error;
    }
  }

  static async getCategory(categoryId: string): Promise<Category | null> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
      const categorySnap = await getDoc(categoryRef);
      if (categorySnap.exists()) {
        const data = categorySnap.data();
        return {
          id: categorySnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Category;
      }
      return null;
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  }
}